import {
  ageOn,
  contactsSchema,
  formatFullName,
  MEMBERSHIP_STATUSES,
  updatePersonSchema,
  type ContactsFormInput,
  type ContactsInput,
  type UpdatePersonFormInput,
  type UpdatePersonInput,
} from '@church/domain'
import {
  ALLOWED_PHOTO_TYPES,
  type PersonDetail,
  notify,
  personPhotoUrl,
  submitSafely,
  useAddHouseholdMember,
  useApplyHouseholdAddress,
  useApplyTag,
  useBackgroundCheck,
  useCampuses,
  useCreateHousehold,
  useCreateNote,
  useDeleteNote,
  useDeletePerson,
  useHouseholds,
  useNoteCategories,
  useNotes,
  usePeopleSearch,
  useAccess,
  usePermissions,
  usePerson,
  usePersonGroups,
  usePersonSubscriptions,
  useRemoveHouseholdMember,
  useRemovePersonPhoto,
  useRemoveTag,
  useReplaceContacts,
  useSetPrimaryContact,
  useTags,
  useUpdateNote,
  useUndoableRemove,
  useUpdatePerson,
  useUploadPersonPhoto,
} from '@church/data'
import {
  faAddressBook,
  faArrowRight,
  faAt,
  faBan,
  faCamera,
  faCheck,
  faChevronRight,
  faCircleCheck,
  faEnvelope,
  faFloppyDisk,
  faHandHoldingDollar,
  faHourglass,
  faHouse,
  faLocationDot,
  faMessage,
  faNoteSticky,
  faPen,
  faPeopleGroup,
  faPeopleRoof,
  faPhone,
  faPlus,
  faShield,
  faStar,
  faThumbtack,
  faTrashCan,
  faTriangleExclamation,
  faUser,
  faUserMinus,
  faUserPlus,
  faUserShield,
  faXmark,
  type IconData,
} from '@church/ui-icons'
import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useId, useState } from 'react'
import type { ReactNode } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { RequireAccess } from '../components/RequireAccess.tsx'
import { BackgroundCheckTab } from '../components/checkin/BackgroundCheckTab.tsx'
import { CommunicationTab } from '../components/messaging/CommunicationTab.tsx'
import { DonorTab } from '../components/giving/DonorTab.tsx'
import { ChildSafetySection } from '../components/people/ChildSafetySection.tsx'
import { CustomFieldSections } from '../components/people/CustomFieldSections.tsx'
import { PrivacyTab } from '../components/people/PrivacyTab.tsx'
import { PersonPermissions } from '../components/access/Access.tsx'
import {
  Button,
  Card,
  Chip,
  ConfirmButton,
  EmptyState,
  ErrorNote,
  ErrorState,
  Field,
  Icon,
  IconButton,
  Input,
  listRow,
  ListSkeleton,
  PanelSection,
  Select,
  SidePanel,
  Spinner,
  Textarea,
} from '../components/ui.tsx'
import { useBreadcrumbs } from '../lib/breadcrumbs.ts'
import { PageAction } from '../components/PageAction.tsx'

export const Route = createFileRoute('/_authed/_shell/people/$id')({
  component: PersonProfileScreen,
})

const todayIso = () => new Date().toISOString().slice(0, 10)

/** The fuller views a summary section opens, each in its own panel. */
type PanelKey =
  'household' | 'groups' | 'notes' | 'giving' | 'communication' | 'background' | 'privacy'

const PANEL_TITLES: Record<PanelKey, string> = {
  household: 'Household',
  groups: 'Groups',
  notes: 'Notes',
  giving: 'Giving',
  communication: 'Communication',
  background: 'Background check',
  privacy: 'Privacy',
}

/**
 * A person, as a summary: the header, then a short section per part of their
 * record — a few lines each, read at a glance — and each section opens its
 * full view (or its editor) in a panel. Sections follow the same permissions
 * the tabs they replace had.
 */
function PersonProfileScreen() {
  const { id } = Route.useParams()
  const personQuery = usePerson(id)
  const { data: person } = personQuery
  const [editing, setEditing] = useState(false)
  const [panel, setPanel] = useState<PanelKey | null>(null)
  const [shownPanel, setShownPanel] = useState<PanelKey>('household')
  const { can } = usePermissions()
  const access = useAccess()
  // One name everywhere: the breadcrumb says what the header says.
  useBreadcrumbs([{ label: person ? formatFullName(person) : 'Person' }])

  if (!person) {
    if (personQuery.isPending) return <ListSkeleton rows={5} />
    const status = (personQuery.error as { status?: unknown } | null)?.status
    // Not there (or not theirs to see) is a different answer from "couldn't ask".
    if (status === 404)
      return (
        <EmptyState
          title="This person isn’t in the directory"
          hint="They may have been removed, or merged into another record."
          action={
            <Link
              to="/people"
              className="inline-flex items-center gap-2 rounded-control bg-brand-solid px-button-x py-button-y text-sm font-medium text-brand-on-solid no-underline hover:bg-brand-solid-hover"
            >
              Back to People
            </Link>
          }
        />
      )
    return (
      <ErrorState
        error={personQuery.error}
        safe="Nothing was changed: their record is safe on the server."
        retrying={personQuery.isRefetching}
        onRetry={() => void personQuery.refetch()}
      />
    )
  }

  const age = ageOn(person.birthdate, todayIso())
  const primaryPhone = person.phones.find((p) => p.isPrimary)?.number ?? person.phones[0]?.number
  const open = (key: PanelKey) => {
    setShownPanel(key)
    setPanel(key)
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <Card className="flex items-start gap-3 p-4 sm:gap-4 sm:p-6">
        <PersonAvatar person={person} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h1 className="text-xl font-bold break-words text-content-default sm:text-2xl">
              {formatFullName(person)}
            </h1>
            <Chip tone={person.membershipStatus === 'member' ? 'brand' : 'neutral'} size="md">
              <span className="capitalize">{person.membershipStatus}</span>
            </Chip>
          </div>
          <p className="mt-0.5 text-sm break-words text-content-muted">
            {[
              age !== null ? `${age} years` : null,
              person.contactsHidden ? 'contact details hidden (minor)' : person.primaryEmail,
              person.contactsHidden ? null : primaryPhone,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
          <div className="mt-3">
            <PersonTags personId={person.id} />
          </div>
        </div>
      </Card>

      <QuickActions
        phone={person.contactsHidden ? undefined : primaryPhone}
        email={person.contactsHidden ? undefined : (person.primaryEmail ?? undefined)}
        onNote={access.has('people', 'editor') ? () => open('notes') : undefined}
      />

      <div className="grid gap-4 lg:grid-cols-2 lg:gap-6">
        <DetailsSection person={person} onEdit={() => setEditing(true)} />
        <ContactSection person={person} />
        <HouseholdSection person={person} onOpen={() => open('household')} />
        <GroupsSection person={person} onOpen={() => open('groups')} />
        <NotesSection person={person} onOpen={() => open('notes')} />
        <ChildSafetySection person={person} />
        <CustomFieldSections person={person} />
        {/* With a login or without one yet: for those who give access, and themself. */}
        <PersonPermissions
          personId={person.id}
          userId={person.userId}
          name={formatFullName(person)}
          email={person.contactsHidden ? null : person.primaryEmail}
        />
        {can('giving:read') && (
          <Section
            icon={faHandHoldingDollar}
            title="Giving"
            action={<SeeAll onClick={() => open('giving')} />}
          >
            <p className="text-sm text-content-muted">
              Gifts, recurring giving and totals. Opening it is recorded in the audit log.
            </p>
          </Section>
        )}
        {access.has('messages', 'viewer') && (
          <CommunicationSection personId={person.id} onOpen={() => open('communication')} />
        )}
        {access.has('checkin', 'administrator') && (
          <BackgroundSection personId={person.id} onOpen={() => open('background')} />
        )}
        {access.has('people', 'administrator', { orgWide: true }) && (
          <Section
            icon={faUserShield}
            title="Privacy"
            action={<SeeAll onClick={() => open('privacy')} />}
          >
            <p className="text-sm text-content-muted">
              Export everything held about {person.firstName}, or erase it on request.
            </p>
          </Section>
        )}
      </div>

      <SidePanel
        open={panel !== null}
        onOpenChange={(next) => {
          if (!next) setPanel(null)
        }}
        title={PANEL_TITLES[shownPanel]}
        description={formatFullName(person)}
      >
        {shownPanel === 'household' && <HouseholdTab person={person} />}
        {shownPanel === 'groups' && <GroupsTab person={person} />}
        {shownPanel === 'notes' && <NotesTab person={person} />}
        {/* Mounted only while open: viewing giving is audited. */}
        {shownPanel === 'giving' && panel === 'giving' && <DonorTab personId={person.id} />}
        {shownPanel === 'communication' && <CommunicationTab personId={person.id} />}
        {shownPanel === 'background' && <BackgroundCheckTab personId={person.id} />}
        {shownPanel === 'privacy' && (
          <PrivacyTab
            person={{ id: person.id, fullName: `${person.firstName} ${person.lastName}` }}
          />
        )}
      </SidePanel>
      {access.has('people', 'editor') && (
        <EditDetailsPanel person={person} open={editing} onClose={() => setEditing(false)} />
      )}
    </div>
  )
}

/**
 * The things done to a person most: call, text, email, note. A row of four
 * under the header, each a large target; one that can't be done (no phone,
 * hidden contacts) is shown greyed rather than moving the others.
 */
function QuickActions({
  phone,
  email,
  onNote,
}: {
  phone?: string
  email?: string
  onNote?: () => void
}) {
  const item =
    'flex min-h-16 flex-col items-center justify-center gap-1 rounded-card bg-surface-raised border border-border-subtle text-xs font-medium no-underline transition-colors duration-(--duration-fast)'
  const live = `${item} text-content-brand hover:bg-surface-sunken active:bg-surface-heading`
  const dead = `${item} text-content-subtle opacity-60`
  const tel = phone?.replace(/[^\d+]/g, '')
  return (
    <nav aria-label="Get in touch" className="grid grid-cols-4 gap-2">
      {tel ? (
        <a href={`tel:${tel}`} className={live}>
          <Icon icon={faPhone} className="size-5" />
          Call
        </a>
      ) : (
        <span className={dead} aria-disabled>
          <Icon icon={faPhone} className="size-5" />
          Call
        </span>
      )}
      {tel ? (
        <a href={`sms:${tel}`} className={live}>
          <Icon icon={faMessage} className="size-5" />
          Text
        </a>
      ) : (
        <span className={dead} aria-disabled>
          <Icon icon={faMessage} className="size-5" />
          Text
        </span>
      )}
      {email ? (
        <a href={`mailto:${email}`} className={live}>
          <Icon icon={faEnvelope} className="size-5" />
          Email
        </a>
      ) : (
        <span className={dead} aria-disabled>
          <Icon icon={faEnvelope} className="size-5" />
          Email
        </span>
      )}
      {onNote ? (
        <button type="button" onClick={onNote} className={live}>
          <Icon icon={faNoteSticky} className="size-5" />
          Note
        </button>
      ) : (
        <span className={dead} aria-disabled>
          <Icon icon={faNoteSticky} className="size-5" />
          Note
        </span>
      )}
    </nav>
  )
}

/** One part of the record on the summary: an icon header, a few lines, an action. */
function Section({
  icon,
  title,
  action,
  children,
}: {
  icon: IconData
  title: string
  action?: ReactNode
  children: ReactNode
}) {
  return (
    <Card className="p-4 sm:p-5">
      <div className="flex min-h-8 items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-content-default">
          <Icon icon={icon} className="size-4 text-content-brand" />
          {title}
        </h2>
        {action && <div className="-my-1 -mr-2 flex items-center">{action}</div>}
      </div>
      <div className="mt-3">{children}</div>
    </Card>
  )
}

function SeeAll({ onClick, label = 'See all' }: { onClick: () => void; label?: string }) {
  return (
    <Button variant="ghost" className="px-2" onClick={onClick}>
      {label}
      <Icon icon={faChevronRight} className="size-3" />
    </Button>
  )
}

function HouseholdSection({ person, onOpen }: { person: PersonDetail; onOpen: () => void }) {
  const { households, isLoading } = useHouseholds(person.id)
  const others = households.flatMap((h) =>
    h.members.filter((m) => m.personId !== person.id).map((m) => ({ ...m, household: h.label })),
  )
  return (
    <Section icon={faPeopleRoof} title="Household" action={<SeeAll onClick={onOpen} />}>
      {isLoading ? (
        <Spinner />
      ) : households.length === 0 ? (
        <p className="text-sm text-content-muted">Not in a household yet.</p>
      ) : (
        <>
          <p className="text-base font-medium text-content-default">
            {households.map((h) => h.label).join(' · ')}
          </p>
          {others.length > 0 && (
            <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
              {others.map((m) => (
                <li key={m.personId} className="text-sm">
                  <Link
                    to="/people/$id"
                    params={{ id: m.personId }}
                    className="text-content-default hover:text-content-brand"
                  >
                    {m.firstName} {m.lastName}
                  </Link>{' '}
                  <span className="capitalize text-content-muted">· {m.role}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Section>
  )
}

function GroupsSection({ person, onOpen }: { person: PersonDetail; onOpen: () => void }) {
  const { data: groups, isPending } = usePersonGroups(person.id)
  const first = (groups ?? []).slice(0, 4)
  const more = (groups?.length ?? 0) - first.length
  return (
    <Section
      icon={faPeopleGroup}
      title="Groups"
      action={groups && groups.length > 0 ? <SeeAll onClick={onOpen} /> : undefined}
    >
      {isPending ? (
        <Spinner />
      ) : first.length === 0 ? (
        <p className="text-sm text-content-muted">Not in any groups.</p>
      ) : (
        <ul className="space-y-2">
          {first.map((group) => (
            <li key={group.id} className="flex items-center justify-between gap-2">
              <Link
                to="/groups/$id"
                params={{ id: group.id }}
                className="truncate text-base text-content-default no-underline hover:text-content-brand"
              >
                {group.name}
              </Link>
              {group.role === 'leader' && <Chip tone="brand">Leads</Chip>}
            </li>
          ))}
          {more > 0 && <li className="text-sm text-content-muted">and {more} more</li>}
        </ul>
      )}
    </Section>
  )
}

function NotesSection({ person, onOpen }: { person: PersonDetail; onOpen: () => void }) {
  const { notes, isLoading } = useNotes(person.id)
  const latest = [...notes].sort((a, b) => Number(b.pinned) - Number(a.pinned)).slice(0, 2)
  return (
    <Section icon={faNoteSticky} title="Notes" action={<SeeAll onClick={onOpen} />}>
      {isLoading ? (
        <Spinner />
      ) : latest.length === 0 ? (
        <p className="text-sm text-content-muted">No notes yet.</p>
      ) : (
        <ul className="space-y-3">
          {latest.map((note) => (
            <li key={note.id}>
              <p className="line-clamp-2 text-sm text-content-default">{note.body}</p>
              <p className="mt-0.5 flex items-center gap-1.5 text-xs text-content-subtle">
                {note.pinned && <Icon icon={faThumbtack} label="Pinned" className="size-3" />}
                {note.categoryName} · {note.authorName ?? 'System'} ·{' '}
                {new Date(note.createdAt).toLocaleDateString()}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Section>
  )
}

function CommunicationSection({ personId, onOpen }: { personId: string; onOpen: () => void }) {
  const { query } = usePersonSubscriptions(personId)
  const s = query.data
  const channels = s
    ? [
        { label: 'Email', on: s.all && s.email },
        { label: 'Texts', on: s.all && s.sms },
      ]
    : []
  return (
    <Section icon={faEnvelope} title="Communication" action={<SeeAll onClick={onOpen} />}>
      {!s ? (
        <Spinner />
      ) : (
        <div className="flex flex-wrap gap-2">
          {channels.map((c) => (
            <Chip key={c.label} tone={c.on ? 'success' : 'neutral'} size="md">
              <Icon icon={c.on ? faCheck : faBan} className="size-3" />
              {c.label}
            </Chip>
          ))}
          {!s.all && (
            <span className="text-sm text-content-muted">Unsubscribed from everything</span>
          )}
        </div>
      )}
    </Section>
  )
}

function BackgroundSection({ personId, onOpen }: { personId: string; onOpen: () => void }) {
  const { data: check, isPending } = useBackgroundCheck(personId)
  return (
    <Section
      icon={faShield}
      title="Background check"
      action={<SeeAll onClick={onOpen} label="Open" />}
    >
      {isPending ? (
        <Spinner />
      ) : !check ? (
        <p className="text-sm text-content-muted">Not started.</p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Chip tone={check.clearedNow ? 'success' : 'warning'} size="md">
            <Icon
              icon={check.clearedNow ? faCircleCheck : faTriangleExclamation}
              className="size-3"
            />
            {check.clearedNow ? 'Can serve with children' : 'Not cleared'}
          </Chip>
          <span className="text-sm capitalize text-content-muted">
            {check.status.replaceAll('_', ' ')}
            {check.expiresAt ? ` · expires ${check.expiresAt}` : ''}
          </span>
        </div>
      )}
    </Section>
  )
}

// ---- Groups ------------------------------------------------------------------

/**
 * The groups this person is in — filtered, server-side, to the ones the viewer
 * may know about. A member of a private group appears here for staff and for
 * that group's leaders, and for nobody else, so the tab can be empty for one
 * person and full for another looking at the same profile.
 */
function GroupsTab({ person }: { person: PersonDetail }) {
  const { data: groups, isPending } = usePersonGroups(person.id)

  if (isPending) return <Spinner />
  if (!groups || groups.length === 0) {
    return (
      <EmptyState
        title="Not in any groups"
        hint="Add them from a group's roster, or they can join one themselves."
      />
    )
  }

  return (
    <Card>
      <ul className="divide-y divide-border-subtle">
        {groups.map((group) => (
          <li key={group.id} className="flex items-center justify-between gap-3 p-3">
            <span className="min-w-0">
              <Link
                to="/groups/$id"
                params={{ id: group.id }}
                className="text-sm text-content-brand hover:underline"
              >
                {group.name}
              </Link>
              <span className="ml-2 text-xs text-content-subtle">
                {[group.groupTypeName, group.scheduleSummary].filter(Boolean).join(' · ')}
              </span>
            </span>
            {group.role === 'leader' && (
              <span className="shrink-0 rounded-full bg-brand-soft px-2 py-0.5 text-xs text-content-brand">
                Leads
              </span>
            )}
          </li>
        ))}
      </ul>
    </Card>
  )
}

/**
 * The avatar, and the only place a photo is changed. Initials stand in when
 * there is no photo — a grey silhouette says "missing", initials say "person".
 */
function PersonAvatar({ person }: { person: PersonDetail }) {
  const access = useAccess()
  const upload = useUploadPersonPhoto(person.id)
  const remove = useRemovePersonPhoto(person.id)
  const editable = access.has('people', 'editor')

  return (
    <div className="flex flex-col items-center gap-1">
      {person.avatarUrl ? (
        <img
          src={personPhotoUrl(person.id, person.avatarUrl)}
          alt={formatFullName(person)}
          className="size-14 rounded-full object-cover"
        />
      ) : (
        <div className="flex size-14 items-center justify-center rounded-full bg-brand-soft text-lg font-semibold text-content-brand">
          {person.firstName[0]}
          {person.lastName[0]}
        </div>
      )}

      {editable && (
        <div className="flex items-center">
          <label
            title={person.avatarUrl ? 'Change photo' : 'Add photo'}
            className="inline-flex size-9 cursor-pointer items-center justify-center rounded-lg text-content-brand hover:bg-surface-sunken"
          >
            <Icon
              icon={upload.isPending ? faHourglass : faCamera}
              label={
                upload.isPending ? 'Uploading…' : person.avatarUrl ? 'Change photo' : 'Add photo'
              }
            />
            <input
              type="file"
              accept={ALLOWED_PHOTO_TYPES.join(',')}
              className="hidden"
              disabled={upload.isPending}
              onChange={(event) => {
                const file = event.target.files?.[0]
                if (file) upload.mutate({ type: file.type, body: file })
              }}
            />
          </label>
          {person.avatarUrl && (
            <IconButton
              icon={faTrashCan}
              label="Remove photo"
              className="size-9 hover:text-danger-solid"
              onClick={() => remove.mutate()}
            />
          )}
        </div>
      )}
      <ErrorNote error={upload.error ?? remove.error} />
    </div>
  )
}

// ---- Tags -------------------------------------------------------------------

const TAG_CHIP_COLORS: Record<string, string> = {
  slate: 'bg-surface-sunken text-content-muted',
  brand: 'bg-brand-soft text-content-brand',
  green: 'bg-success-soft text-success-content',
  amber: 'bg-warning-soft text-warning-content',
  red: 'bg-danger-soft text-danger-content',
  purple: 'bg-surface-sunken text-content-default',
  teal: 'bg-surface-sunken text-content-default',
  pink: 'bg-surface-sunken text-content-default',
}

function PersonTags({ personId }: { personId: string }) {
  const access = useAccess()
  const applied = useTags(personId)
  const all = useTags()
  const apply = useApplyTag()
  const remove = useRemoveTag()
  const editable = access.has('people', 'editor')

  const appliedIds = new Set((applied.data ?? []).map((t) => t.id))
  const available = (all.data ?? []).filter((t) => !appliedIds.has(t.id))

  return (
    <div className="flex flex-wrap items-center gap-2">
      {(applied.data ?? []).map((tag) => (
        <span
          key={tag.id}
          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${
            TAG_CHIP_COLORS[tag.color ?? 'slate'] ?? TAG_CHIP_COLORS.slate
          }`}
        >
          {tag.name}
          {editable && (
            <button
              type="button"
              aria-label={`Remove ${tag.name}`}
              className="-my-1 -mr-1.5 inline-flex size-7 items-center justify-center rounded-full text-current opacity-60 hover:opacity-100"
              onClick={() =>
                remove.mutate(
                  { tagId: tag.id, personIds: [personId] },
                  {
                    onSuccess: () =>
                      notify({
                        title: `Removed the tag “${tag.name}”`,
                        action: {
                          label: 'Undo',
                          run: () => apply.mutate({ tagId: tag.id, personIds: [personId] }),
                        },
                      }),
                  },
                )
              }
            >
              <Icon icon={faXmark} className="size-3" />
            </button>
          )}
        </span>
      ))}

      {editable && available.length > 0 && (
        <Select
          aria-label="Add tag"
          className="max-w-40"
          value=""
          onChange={(e) => {
            const tag = available.find((t) => t.id === e.target.value)
            if (tag)
              apply.mutate(
                { tagId: tag.id, personIds: [personId] },
                { onSuccess: () => notify({ title: `Tagged “${tag.name}”` }) },
              )
          }}
        >
          <option value="">Add tag…</option>
          {available.map((tag) => (
            <option key={tag.id} value={tag.id}>
              {tag.name}
            </option>
          ))}
        </Select>
      )}
      <ErrorNote error={apply.error ?? remove.error} />
    </div>
  )
}

// ---- Notes ------------------------------------------------------------------

function NotesTab({ person }: { person: PersonDetail }) {
  const access = useAccess()
  const { notes, hasNextPage, fetchNextPage, isLoading } = useNotes(person.id)
  const { data: categories } = useNoteCategories()
  const create = useCreateNote(person.id)
  const update = useUpdateNote(person.id)
  const remove = useDeleteNote(person.id)
  const [adding, setAdding] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)
  const [shownId, setShownId] = useState<string | null>(null)

  const [body, setBody] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [pinned, setPinned] = useState(false)
  const shown = notes.find((n) => n.id === shownId)

  const submit = () => {
    if (body.trim() === '') return
    create.mutate(
      { body, categoryId: categoryId || null, pinned },
      {
        onSuccess: () => {
          setBody('')
          setPinned(false)
          setAdding(false)
        },
      },
    )
  }

  return (
    <div className="space-y-4">
      <RequireAccess module="people" level="editor">
        <div className="flex justify-end max-sm:hidden">
          <PageAction icon={faPlus} label="New note" onClick={() => setAdding(true)}>
            Note
          </PageAction>
        </div>
      </RequireAccess>

      {isLoading && <Spinner />}
      {!isLoading && notes.length === 0 && (
        <EmptyState
          title="No notes"
          hint={
            access.has('people', 'editor')
              ? 'Notes are visible to everyone who can read their category.'
              : undefined
          }
        />
      )}

      {notes.map((note) => {
        const content = (
          <>
            <span className="flex flex-wrap items-center gap-2 text-xs text-content-subtle">
              {note.pinned && (
                <Chip tone="warning">
                  <Icon icon={faThumbtack} className="size-3" />
                  Pinned
                </Chip>
              )}
              <Chip>{note.categoryName}</Chip>
              <span>
                {note.authorName ?? 'System'} · {new Date(note.createdAt).toLocaleDateString()}
              </span>
            </span>
            <span className="mt-2 block whitespace-pre-wrap text-sm text-content-default">
              {note.body}
            </span>
          </>
        )
        return (
          <Card key={note.id} className="overflow-hidden">
            {note.canEdit ? (
              <button
                type="button"
                onClick={() => {
                  setShownId(note.id)
                  setOpenId(note.id)
                }}
                className="block w-full p-4 text-left hover:bg-surface-sunken"
              >
                {content}
              </button>
            ) : (
              <div className="p-4">{content}</div>
            )}
          </Card>
        )
      })}

      {hasNextPage && (
        <Button variant="secondary" onClick={() => void fetchNextPage()}>
          Load older notes
        </Button>
      )}
      <ErrorNote error={update.error ?? remove.error} />

      <SidePanel
        open={openId !== null && shown !== undefined}
        onOpenChange={(next) => {
          if (!next) setOpenId(null)
        }}
        title="Note"
        description={
          shown
            ? `${shown.categoryName} · ${shown.authorName ?? 'System'} · ${new Date(shown.createdAt).toLocaleDateString()}`
            : undefined
        }
        footer={
          shown && (
            <ConfirmButton
              label="Delete note"
              icon={faTrashCan}
              variant="quietDanger"
              className="-ml-4"
              title="Delete this note?"
              description="It disappears for everyone who can read this category."
              confirmLabel="Delete note"
              onConfirm={() => remove.mutate(shown.id, { onSuccess: () => setOpenId(null) })}
            />
          )
        }
      >
        {shown && (
          <>
            <p className="whitespace-pre-wrap text-base text-content-default">{shown.body}</p>
            <PanelSection title="Pinned" hint="Pinned notes stay at the top of this tab.">
              <Button
                variant="secondary"
                icon={faThumbtack}
                onClick={() => update.mutate({ noteId: shown.id, pinned: !shown.pinned })}
              >
                {shown.pinned ? 'Unpin' : 'Pin to top'}
              </Button>
            </PanelSection>
          </>
        )}
      </SidePanel>

      <SidePanel
        open={adding}
        onOpenChange={(next) => {
          if (!next) setAdding(false)
        }}
        title="Add a note"
        description={`About ${person.firstName}. Its category decides who can read it.`}
        primary={
          <Button
            icon={faPlus}
            className="py-1.5"
            pending={create.isPending}
            pendingLabel="Saving…"
            disabled={body.trim() === ''}
            onClick={submit}
          >
            Add note
          </Button>
        }
      >
        <Textarea
          rows={6}
          aria-label="Note"
          placeholder={`Note about ${person.firstName}…`}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <Field label="Category">
          <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">General</option>
            {(categories ?? [])
              .filter((c) => !c.isDefault)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </Select>
        </Field>
        <label className="flex items-center gap-2 text-sm text-content-muted">
          <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} />
          Pin to top
        </label>
        <ErrorNote error={create.error} />
      </SidePanel>
    </div>
  )
}

// ---- Overview ---------------------------------------------------------------

const longDate = (date: string) =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

/**
 * The facts, read at a glance. What is not recorded is named once, quietly,
 * rather than as a column of dashes; changing anything happens in the panel.
 */
function DetailsSection({ person, onEdit }: { person: PersonDetail; onEdit: () => void }) {
  const { data: campuses } = useCampuses()
  const access = useAccess()
  const age = ageOn(person.birthdate, todayIso())
  const facts: [string, string | null][] = [
    [
      'Birthday',
      person.birthdate
        ? `${longDate(person.birthdate)}${age !== null ? ` · ${age} years` : ''}`
        : null,
    ],
    [
      'Campus',
      person.campusId ? (campuses?.find((c) => c.id === person.campusId)?.name ?? '—') : null,
    ],
    ['Nickname', person.nickname],
    ['Gender', person.gender],
    ['Marital status', person.maritalStatus],
    ['Anniversary', person.anniversary ? longDate(person.anniversary) : null],
  ]
  const known = facts.filter(([, value]) => value)
  const missing = facts.filter(([, value]) => !value).map(([label]) => label.toLowerCase())

  return (
    <Section
      icon={faUser}
      title="Details"
      action={
        access.has('people', 'editor') ? (
          <IconButton icon={faPen} label="Edit details" onClick={onEdit} />
        ) : undefined
      }
    >
      <dl className="space-y-3">
        {known.map(([label, value]) => (
          <div key={label}>
            <dt className="text-xs text-content-subtle">{label}</dt>
            <dd className="text-base text-content-default">{value}</dd>
          </div>
        ))}
      </dl>
      {missing.length > 0 && (
        <p className="mt-4 text-xs text-content-subtle">Not recorded: {missing.join(', ')}.</p>
      )}
    </Section>
  )
}

const CONTACT_ICONS = { email: faAt, phone: faPhone, address: faLocationDot } as const

/** How to reach them, primary first; every email, phone and address is edited in the panel. */
function ContactSection({ person }: { person: PersonDetail }) {
  const access = useAccess()
  const [editing, setEditing] = useState(false)
  const replace = useReplaceContacts(person.id)
  const formId = useId()
  if (person.contactsHidden) {
    return (
      <Section icon={faAddressBook} title="Contact">
        <p className="text-sm text-content-muted">
          Hidden — this person is under 18. Reach them through their household.
        </p>
      </Section>
    )
  }
  const editable = access.has('people', 'editor')
  const lines = [
    ...person.emails.map((e) => ({
      key: e.id,
      kind: 'email' as const,
      value: e.email,
      label: e.label,
      primary: e.isPrimary,
    })),
    ...person.phones.map((p) => ({
      key: p.id,
      kind: 'phone' as const,
      value: p.number,
      label: p.label,
      primary: p.isPrimary,
    })),
    ...person.addresses.map((a) => ({
      key: a.id,
      kind: 'address' as const,
      value: formatAddress(a),
      label: a.label,
      primary: a.isPrimary,
    })),
  ]
  return (
    <Section
      icon={faAddressBook}
      title="Contact"
      action={
        editable ? (
          <IconButton
            icon={faPen}
            label="Edit contacts"
            onClick={() => {
              replace.reset()
              setEditing(true)
            }}
          />
        ) : undefined
      }
    >
      {lines.length === 0 ? (
        <p className="text-sm text-content-muted">No email, phone or address yet.</p>
      ) : (
        <ul className="space-y-3">
          {lines.map((line) => (
            <li key={line.key} className="flex items-start gap-3">
              <Icon icon={CONTACT_ICONS[line.kind]} className="mt-1 size-4 text-content-subtle" />
              <span className="min-w-0 flex-1">
                <span className="block text-base break-words text-content-default">
                  {line.value}
                </span>
                {line.label && <span className="text-xs text-content-subtle">{line.label}</span>}
              </span>
              {line.primary && <Chip>Primary</Chip>}
            </li>
          ))}
        </ul>
      )}
      {editable && (
        <SidePanel
          open={editing}
          onOpenChange={(next) => {
            if (!next) setEditing(false)
          }}
          title="Edit contacts"
          description={formatFullName(person)}
          primary={
            <Button
              type="submit"
              form={formId}
              icon={faFloppyDisk}
              className="py-1.5"
              pending={replace.isPending}
              pendingLabel="Saving…"
            >
              Save
            </Button>
          }
        >
          <ContactsEditor
            key={String(editing)}
            person={person}
            replace={replace}
            formId={formId}
            onSaved={() => setEditing(false)}
          />
        </SidePanel>
      )}
    </Section>
  )
}

const formatAddress = (a: PersonDetail['addresses'][number]) =>
  [a.street, a.street2, [a.city, a.state].filter(Boolean).join(', '), a.postalCode, a.country]
    .filter(Boolean)
    .join(' · ') || 'Address'

/** The person's details, edited in a panel; retiring the record lives in its footer. */
function EditDetailsPanel({
  person,
  open,
  onClose,
}: {
  person: PersonDetail
  open: boolean
  onClose: () => void
}) {
  const update = useUpdatePerson(person.id)
  const remove = useDeletePerson()
  const navigate = useNavigate()
  const access = useAccess()
  const { data: campuses } = useCampuses()

  const form = useForm<UpdatePersonFormInput, unknown, UpdatePersonInput>({
    resolver: zodResolver(updatePersonSchema),
    defaultValues: {
      firstName: person.firstName,
      lastName: person.lastName,
      nickname: person.nickname,
      gender: person.gender,
      birthdate: person.birthdate,
      anniversary: person.anniversary,
      maritalStatus: person.maritalStatus,
      membershipStatus: person.membershipStatus as UpdatePersonFormInput['membershipStatus'],
      campusId: person.campusId,
    },
  })

  const [discarding, setDiscarding] = useState(false)
  const removeLater = useUndoableRemove()
  const dirty = form.formState.isDirty
  const name = formatFullName(person)

  const submit = form.handleSubmit(async (input) => {
    await update.mutateAsync(input)
    form.reset(input)
    notify({ title: `Saved ${input.firstName ?? person.firstName}’s details` })
    onClose()
  })

  const close = () => {
    setDiscarding(false)
    form.reset()
    onClose()
  }

  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => {
        if (next) return
        // Closing with unsaved changes asks first, in the footer.
        if (dirty) setDiscarding(true)
        else close()
      }}
      title="Edit details"
      description={name}
      footer={
        discarding ? (
          <span className="text-sm font-medium text-content-default">Discard your changes?</span>
        ) : access.has('people', 'administrator') ? (
          <Button
            variant="quietDanger"
            icon={faTrashCan}
            className="-ml-4"
            onClick={() => {
              onClose()
              void navigate({ to: '/people' })
              removeLater({
                id: person.id,
                title: `Removed ${name} from the directory`,
                description: 'Giving history and attendance still point at the record.',
                delay: 8000,
                commit: () => remove.mutateAsync(person.id),
              })
            }}
          >
            Remove from the directory
          </Button>
        ) : undefined
      }
      primary={
        discarding ? (
          <>
            <Button variant="secondary" className="py-1.5" onClick={() => setDiscarding(false)}>
              Keep editing
            </Button>
            <Button variant="danger" className="py-1.5" onClick={close}>
              Discard
            </Button>
          </>
        ) : (
          <>
            {!dirty && <span className="text-sm text-content-subtle">No changes</span>}
            <Button
              type="submit"
              form="edit-person-details"
              icon={faFloppyDisk}
              className="py-1.5"
              disabled={!dirty}
              pending={update.isPending}
              pendingLabel="Saving…"
            >
              Save
            </Button>
          </>
        )
      }
    >
      <form
        id="edit-person-details"
        onSubmit={submitSafely(submit)}
        className="space-y-4"
        noValidate
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="First name" error={form.formState.errors.firstName?.message}>
            <Input {...form.register('firstName')} />
          </Field>
          <Field label="Last name" error={form.formState.errors.lastName?.message}>
            <Input {...form.register('lastName')} />
          </Field>
          <Field label="Nickname">
            <Input {...form.register('nickname', { setValueAs: (v: string) => v || null })} />
          </Field>
          <Field label="Gender">
            <Select {...form.register('gender', { setValueAs: (v: string) => v || null })}>
              <option value="">Not recorded</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              {person.gender && !['Male', 'Female'].includes(person.gender) && (
                <option value={person.gender}>{person.gender}</option>
              )}
            </Select>
          </Field>
          <Field label="Birthdate" error={form.formState.errors.birthdate?.message}>
            <Input
              type="date"
              {...form.register('birthdate', { setValueAs: (v: string) => v || null })}
            />
          </Field>
          <Field label="Anniversary" error={form.formState.errors.anniversary?.message}>
            <Input
              type="date"
              {...form.register('anniversary', { setValueAs: (v: string) => v || null })}
            />
          </Field>
          <Field label="Status">
            <Select {...form.register('membershipStatus')}>
              {MEMBERSHIP_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Campus">
            <Select
              {...form.register('campusId', { setValueAs: (v: string) => (v === '' ? null : v) })}
            >
              <option value="">No campus</option>
              {(campuses ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <ErrorNote error={update.error} />
      </form>
    </SidePanel>
  )
}

// ---- Contacts ----------------------------------------------------------------

function ContactsEditor({
  person,
  replace,
  formId,
  onSaved,
}: {
  person: PersonDetail
  replace: ReturnType<typeof useReplaceContacts>
  formId: string
  onSaved: () => void
}) {
  const form = useForm<ContactsFormInput, unknown, ContactsInput>({
    resolver: zodResolver(contactsSchema),
    defaultValues: {
      emails: person.emails.map((e) => ({
        email: e.email,
        label: e.label,
        isPrimary: e.isPrimary,
      })),
      phones: person.phones.map((p) => ({
        number: p.number,
        label: p.label,
        isPrimary: p.isPrimary,
      })),
      addresses: person.addresses.map((a) => ({
        street: a.street,
        street2: a.street2,
        city: a.city,
        state: a.state,
        postalCode: a.postalCode,
        country: a.country,
        label: a.label,
        isPrimary: a.isPrimary,
      })),
    },
  })

  const emails = useFieldArray({ control: form.control, name: 'emails' })
  const phones = useFieldArray({ control: form.control, name: 'phones' })
  const addresses = useFieldArray({ control: form.control, name: 'addresses' })

  const submit = form.handleSubmit(async (input) => {
    await replace.mutateAsync(input)
    form.reset(input)
    onSaved()
  })

  return (
    <form id={formId} onSubmit={submitSafely(submit)} className="space-y-5" noValidate>
      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-content-default">Emails</h2>
        {emails.fields.map((field, i) => (
          <div key={field.id} className="flex flex-wrap items-end gap-2">
            <div className="w-full sm:w-auto sm:flex-1">
              <Field label="Email" error={form.formState.errors.emails?.[i]?.email?.message}>
                <Input type="email" {...form.register(`emails.${i}.email`)} />
              </Field>
            </div>
            <label className="flex items-center gap-1 pb-2 text-sm text-content-muted">
              <input type="checkbox" {...form.register(`emails.${i}.isPrimary`)} /> primary
            </label>
            <IconButton icon={faTrashCan} label="Remove email" onClick={() => emails.remove(i)} />
          </div>
        ))}
        <Button variant="secondary" icon={faPlus} onClick={() => emails.append({ email: '' })}>
          Email
        </Button>
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-content-default">Phones</h2>
        {phones.fields.map((field, i) => (
          <div key={field.id} className="flex flex-wrap items-end gap-2">
            <div className="w-full sm:w-auto sm:flex-1">
              <Field label="Phone" error={form.formState.errors.phones?.[i]?.number?.message}>
                <Input {...form.register(`phones.${i}.number`)} />
              </Field>
            </div>
            <label className="flex items-center gap-1 pb-2 text-sm text-content-muted">
              <input type="checkbox" {...form.register(`phones.${i}.isPrimary`)} /> primary
            </label>
            <IconButton icon={faTrashCan} label="Remove phone" onClick={() => phones.remove(i)} />
          </div>
        ))}
        <Button variant="secondary" icon={faPlus} onClick={() => phones.append({ number: '' })}>
          Phone
        </Button>
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-content-default">Addresses</h2>
        {addresses.fields.map((field, i) => (
          <div key={field.id} className="space-y-2 rounded-lg border border-border-subtle p-3">
            <div className="grid gap-2 sm:grid-cols-2">
              <Field label="Street">
                <Input {...form.register(`addresses.${i}.street`)} />
              </Field>
              <Field label="City">
                <Input {...form.register(`addresses.${i}.city`)} />
              </Field>
              <Field label="State">
                <Input {...form.register(`addresses.${i}.state`)} />
              </Field>
              <Field label="Postal code">
                <Input {...form.register(`addresses.${i}.postalCode`)} />
              </Field>
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-1 text-sm text-content-muted">
                <input type="checkbox" {...form.register(`addresses.${i}.isPrimary`)} /> primary
              </label>
              <IconButton
                icon={faTrashCan}
                label="Remove address"
                onClick={() => addresses.remove(i)}
              />
            </div>
          </div>
        ))}
        <Button variant="secondary" icon={faPlus} onClick={() => addresses.append({})}>
          Address
        </Button>
      </section>

      <ErrorNote error={replace.error} />
    </form>
  )
}

// ---- Household ----------------------------------------------------------------

function HouseholdTab({ person }: { person: PersonDetail }) {
  const { households, isLoading } = useHouseholds(person.id)
  const createHousehold = useCreateHousehold()

  if (isLoading) return <Spinner />

  return (
    <div className="space-y-4">
      {households.length === 0 ? (
        <Card>
          <EmptyState title="Not in a household yet" />
        </Card>
      ) : (
        households.map((h) => <HouseholdCard key={h.id} householdId={h.id} personId={person.id} />)
      )}

      <RequireAccess module="people" level="editor">
        <div>
          <Button
            variant="secondary"
            icon={faPlus}
            disabled={createHousehold.isPending}
            onClick={() =>
              createHousehold.mutate({ members: [{ personId: person.id, role: 'adult' }] })
            }
          >
            {createHousehold.isPending ? 'Creating…' : 'Start a new household'}
          </Button>
          <ErrorNote error={createHousehold.error} />
        </div>
      </RequireAccess>
    </div>
  )
}

function HouseholdCard({ householdId, personId }: { householdId: string; personId: string }) {
  const { households } = useHouseholds(personId)
  const access = useAccess()
  const household = households.find((h) => h.id === householdId)
  const addMember = useAddHouseholdMember(householdId)
  const removeMember = useRemoveHouseholdMember(householdId)
  const setPrimary = useSetPrimaryContact(householdId)
  const applyAddress = useApplyHouseholdAddress(householdId)
  const [adding, setAdding] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)
  const [shownId, setShownId] = useState<string | null>(null)
  const [picker, setPicker] = useState('')
  const search = usePeopleSearch(picker)

  if (!household) return null
  const editable = access.has('people', 'editor')
  const shown = household.members.find((m) => m.personId === shownId)
  const mutationError =
    addMember.error ?? removeMember.error ?? setPrimary.error ?? applyAddress.error

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border-subtle px-4 py-3">
        <h2 className="font-semibold text-content-default">{household.label}</h2>
        {editable && (
          <div className="-mr-2 flex gap-1">
            <IconButton
              icon={faHouse}
              label="Apply the primary contact’s address to everyone"
              disabled={applyAddress.isPending}
              onClick={() => applyAddress.mutate()}
            />
            <IconButton icon={faUserPlus} label="Add member" onClick={() => setAdding(true)} />
          </div>
        )}
      </div>

      <ul className="divide-y divide-border-subtle">
        {household.members.map((m) => (
          <li key={m.personId}>
            <button
              type="button"
              disabled={!editable}
              onClick={() => {
                setShownId(m.personId)
                setOpenId(m.personId)
              }}
              className={`${listRow} flex w-full items-center gap-3 text-left enabled:hover:bg-surface-sunken`}
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-base font-medium text-content-default">
                  {m.firstName} {m.lastName}
                </span>
                <span className="text-sm capitalize text-content-muted">{m.role}</span>
              </span>
              {household.primaryContactPersonId === m.personId && (
                <Chip tone="brand">Primary contact</Chip>
              )}
            </button>
          </li>
        ))}
      </ul>
      <div className="px-4">
        <ErrorNote error={mutationError} />
      </div>

      {editable && (
        <>
          <SidePanel
            open={openId !== null && shown !== undefined}
            onOpenChange={(next) => {
              if (!next) setOpenId(null)
            }}
            title={shown ? `${shown.firstName} ${shown.lastName}` : ''}
            description={shown ? `${household.label} · ${shown.role}` : undefined}
            footer={
              shown && (
                <ConfirmButton
                  label="Remove from household"
                  icon={faUserMinus}
                  variant="quietDanger"
                  className="-ml-4"
                  title={`Remove ${shown.firstName} from ${household.label}?`}
                  description="They stay in the directory; only the household link goes."
                  confirmLabel="Remove"
                  onConfirm={() =>
                    removeMember.mutate(shown.personId, { onSuccess: () => setOpenId(null) })
                  }
                />
              )
            }
          >
            {shown && (
              <>
                <PanelSection
                  title="Primary contact"
                  hint="The house icon on the household copies the primary contact’s address to everyone."
                >
                  {household.primaryContactPersonId === shown.personId ? (
                    <Chip tone="brand" size="lg">
                      <Icon icon={faCheck} />
                      Primary contact
                    </Chip>
                  ) : (
                    <Button
                      variant="secondary"
                      icon={faStar}
                      disabled={setPrimary.isPending}
                      onClick={() => setPrimary.mutate(shown.personId)}
                    >
                      Make primary contact
                    </Button>
                  )}
                </PanelSection>
                {shown.personId !== personId && (
                  <div className="border-t border-border-subtle pt-4">
                    <Link
                      to="/people/$id"
                      params={{ id: shown.personId }}
                      className="flex items-center gap-3 rounded-lg px-1 py-2 text-sm font-medium text-content-brand hover:bg-surface-sunken"
                    >
                      <Icon icon={faUser} />
                      <span className="flex-1">{shown.firstName}’s profile</span>
                      <Icon icon={faArrowRight} />
                    </Link>
                  </div>
                )}
              </>
            )}
          </SidePanel>
          <SidePanel
            open={adding}
            onOpenChange={(next) => {
              if (!next) {
                setAdding(false)
                setPicker('')
              }
            }}
            title="Add to the household"
            description={household.label}
          >
            <Input
              placeholder="Search people…"
              aria-label="Search people"
              value={picker}
              onChange={(e) => setPicker(e.target.value)}
            />
            {picker.trim().length >= 2 && (
              <ul className="divide-y divide-border-subtle">
                {search.results
                  .filter((r) => !household.members.some((m) => m.personId === r.id))
                  .slice(0, 8)
                  .map((r) => (
                    <li key={r.id}>
                      <button
                        type="button"
                        disabled={addMember.isPending}
                        className="flex w-full items-center justify-between gap-2 px-1 py-3 text-left text-sm hover:bg-surface-sunken"
                        onClick={() => addMember.mutate({ personId: r.id })}
                      >
                        <span className="text-content-default">
                          {r.firstName} {r.lastName}
                        </span>
                        <Icon icon={faPlus} label="Add" className="size-4 text-content-brand" />
                      </button>
                    </li>
                  ))}
              </ul>
            )}
            <ErrorNote error={addMember.error} />
          </SidePanel>
        </>
      )}
    </Card>
  )
}
